import { useEffect, useState } from 'react'
import { Icon } from './Icon'
import { useDialog } from '../useDialog'

/**
 * A short walk around one screen, pointing at the real controls.
 *
 * Not a modal that describes the product in the abstract: each stop names an
 * element that is on the screen right now, lifts it out of the dimming, and
 * says what it is for. Somebody who has just arrived at a documentation tool
 * does not need a tour of documentation — they need to be told which of these
 * eleven controls writes a page and which one publishes it.
 *
 * Offered once per person per surface (the server remembers, so it does not
 * come back on a second machine) and replayable from Help, because the one
 * thing worse than a tour nobody asked for is a tour nobody can find again.
 *
 * Every stop is skippable and Escape ends it: guidance that traps somebody is
 * not guidance.
 */

export interface Stop {
  /** A CSS selector for the control this stop is about. */
  at: string
  title: string
  says: string
  /** Which side of the control the card sits on. */
  side?: 'top' | 'bottom' | 'left' | 'right'
}

export function Tour({
  stops,
  surface,
  onDone,
}: {
  stops: Stop[]
  /** Named for the report and for the "seen" record: 'pages', 'publish'… */
  surface: string
  /** Called when it ends, however it ends — finished, skipped or escaped. */
  onDone: (finished: boolean) => void
}) {
  const [index, setIndex] = useState(0)
  const [box, setBox] = useState<DOMRect | null>(null)
  const stop = stops[index]

  /*
   * Measured every time the stop changes, and again on a resize.
   *
   * A tour that remembers where a control WAS is a tour that points at empty
   * space the moment a drawer opens or the window is dragged narrower.
   */
  useEffect(() => {
    if (!stop) return
    const measure = () => {
      const node = document.querySelector(stop.at)
      setBox(node ? node.getBoundingClientRect() : null)
    }
    measure()
    // A stop whose control is inside something that has just been opened may
    // not be laid out yet on the first frame.
    const soon = window.setTimeout(measure, 120)
    window.addEventListener('resize', measure)
    window.addEventListener('scroll', measure, true)
    return () => {
      window.clearTimeout(soon)
      window.removeEventListener('resize', measure)
      window.removeEventListener('scroll', measure, true)
    }
  }, [stop])

  /*
   * The keyboard, through the one hook that holds it.
   *
   * A tour dims the screen and speaks for it, which makes it a modal dialog
   * whatever else it is: focus belongs inside the card, Escape ends it, and
   * the keyboard goes back where it came from afterwards. The arrows below
   * are the tour's own, on top of that.
   */
  const panel = useDialog<HTMLDivElement>(true, () => onDone(false))

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'Enter') {
        e.preventDefault()
        setIndex((i) => {
          if (i + 1 >= stops.length) {
            onDone(true)
            return i
          }
          return i + 1
        })
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault()
        setIndex((i) => Math.max(0, i - 1))
      }
    }
    window.addEventListener('keydown', onKey, { capture: true })
    return () => window.removeEventListener('keydown', onKey, { capture: true })
  }, [stops.length, onDone])

  if (!stop) return null
  const last = index + 1 >= stops.length

  /*
   * Where the card goes.
   *
   * Beside the control it is about when there is room, and in the middle of
   * the screen when the control could not be found at all — a stop about
   * something that is not on this screen is still worth reading, and moving
   * it to the centre is more honest than pinning it to the corner.
   */
  const side = stop.side ?? 'right'
  const CARD = { w: 300, h: 150 }
  const gap = 14
  const placed = box
    ? {
        left:
          side === 'right'
            ? Math.min(box.right + gap, window.innerWidth - CARD.w - gap)
            : side === 'left'
              ? Math.max(gap, box.left - gap - CARD.w)
              : Math.min(
                  Math.max(gap, box.left + box.width / 2 - CARD.w / 2),
                  window.innerWidth - CARD.w - gap,
                ),
        top:
          side === 'bottom'
            ? Math.min(box.bottom + gap, window.innerHeight - CARD.h - gap)
            : side === 'top'
              ? Math.max(gap, box.top - gap - CARD.h)
              : Math.min(
                  Math.max(gap, box.top + box.height / 2 - CARD.h / 2),
                  window.innerHeight - CARD.h - gap,
                ),
      }
    : {
        left: window.innerWidth / 2 - CARD.w / 2,
        top: window.innerHeight / 2 - CARD.h / 2,
      }

  return (
    <div className="tour" role="dialog" aria-modal="true" aria-label={`${surface} tour`}>
      {/* The dimming, with a hole cut where the control is. Four panels
          rather than an SVG mask: the control keeps its own hover and focus
          styles, which is what makes the stop feel like it is about a real
          thing rather than a picture of one. */}
      <div className="tour__dim" onMouseDown={() => onDone(false)} />
      {box && (
        <div
          className="tour__spot"
          style={{
            left: `${box.left - 6}px`,
            top: `${box.top - 6}px`,
            width: `${box.width + 12}px`,
            height: `${box.height + 12}px`,
          }}
        />
      )}
      <div
        className="tour__card"
        ref={panel}
        style={{ left: `${placed.left}px`, top: `${placed.top}px` }}
      >
        <div className="tour__count">
          Step {index + 1} of {stops.length}
        </div>
        <h3 className="tour__title">{stop.title}</h3>
        <p className="tour__says">{stop.says}</p>
        <div className="tour__actions">
          <button className="btn btn--ghost btn--sm" onClick={() => onDone(false)}>
            Skip the tour
          </button>
          <span className="tour__spacer" />
          {index > 0 && (
            <button className="btn btn--outline btn--sm" onClick={() => setIndex(index - 1)}>
              Back
            </button>
          )}
          <button
            className="btn btn--primary btn--sm"
            data-autofocus
            onClick={() => (last ? onDone(true) : setIndex(index + 1))}
          >
            {last ? 'Done' : 'Next'}
            {!last && <Icon name="chevron-right" size={13} />}
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * The tours themselves, keyed by surface.
 *
 * Written beside the component rather than in each screen so the whole set
 * can be read at once: five stops is the budget for the screen somebody lives
 * on, two for the ones they visit.
 */
/**
 * A tour, and whether it arrives on its own.
 *
 * `askedFor` means Help offers it and nothing else does. Two tours are like
 * that and for the same reason: a walk that dims the screen and takes the
 * keyboard is an interruption, and there are moments where interrupting is
 * indefensible however good the walk is.
 */
export interface TourDefinition {
  label: string
  stops: Stop[]
  /** Offered from Help only — never thrown at somebody on arrival. */
  askedFor?: true
}

export const TOURS: Record<string, TourDefinition> = {
  /**
   * Home, which is the screen everybody lands on and the only one that had no
   * walk.
   *
   * It is not a screen you DO anything on — it is four numbers and six cards,
   * every one of which is a door to somewhere else — and that is exactly what
   * makes it worth five stops: a person looking at it cannot tell which of
   * those numbers is a summary and which is a thing waiting for them. The
   * tours that existed were written for the screens with obvious controls.
   */
  home: {
    label: 'What Home is telling you',
    /*
     * From Help, not on arrival — and this was learned by building it the
     * other way first.
     *
     * A tour is a modal: it dims the screen and holds the keyboard, which is
     * right for a walk somebody asked for and wrong for the screen everybody
     * lands on. Home is where the first run leaves you and where every reload
     * returns you, so an unbidden one arrives immediately after the guided
     * first run — the two guides talking at once this file already refuses —
     * and again at every arrival until it is answered. The keyboard audit put
     * it plainly: it reported the keyboard still held by a dialog nobody had
     * opened.
     *
     * The walk itself is worth having, which is why it exists. `publish` is
     * ask-only for the same shape of reason, written beside it.
     */
    askedFor: true,
    stops: [
      {
        at: '.screen__head',
        side: 'bottom',
        title: 'Which site, and how current it is',
        says: 'The line under the heading is the whole state of this documentation in one sentence: which site you are looking at, how many pages it has, and when it last reached readers. If it says never published, nothing here is on the internet yet.',
      },
      {
        at: '.stats',
        side: 'bottom',
        title: 'One question, asked four ways',
        says: 'These are not four separate counts. They are how Pulse sorts every page — current, due a recheck, outdated — and what is waiting in the inbox. Each one opens the screen behind it, so a number you do not like is one press from the pages that made it.',
      },
      {
        at: '[data-home-warnings]',
        side: 'top',
        title: 'Worth knowing comes first',
        says: 'Anything that needs a decision — a payment that did not go through, an allowance nearly spent, a domain waiting on DNS — appears here and nowhere else. When there is nothing to say, the card is not drawn at all, so an empty Home is good news rather than a screen that failed to load.',
      },
      {
        at: '[data-home-attention]',
        side: 'top',
        title: 'The pages, worst first',
        says: 'Needs your attention is the shortest useful list: the pages Pulse is least sure about, in the order it would fix them. Recently edited beside it is the other way round — where you were, so you can get back to it.',
      },
      {
        at: '[data-nav="pages"]',
        side: 'right',
        title: 'And writing is one press away',
        says: 'Nothing on Home changes the documentation. Every card is a door; the writing happens in Pages, and the tour there walks you round it.',
      },
    ],
  },
  pages: {
    label: 'Writing and publishing',
    stops: [
      {
        at: '.sidenav__tree, .rail',
        side: 'right',
        title: 'Everything readers see',
        says: 'This tree IS the published navigation — the order here is the order on the site. Drag a page between two rows to reorder it, or onto one to nest it underneath. The + on a row adds a page under that row, and the ⋯ renames, duplicates or bins it. ⌥ with an arrow key does the same without the mouse.',
      },
      {
        at: '.rail__foot-act',
        side: 'right',
        title: 'A new page asks where it goes',
        says: 'It wants a title and a place in the tree. A page nothing lists cannot be published, so it is asked now rather than reported later.',
      },
      {
        at: '.viewbar',
        side: 'bottom',
        title: 'Write it, then read it',
        says: 'Edit is where the words go; Preview is what a reader gets. The button beside them holds the Markdown behind the page and a comparison with what is live.',
      },
      {
        at: '.top__save',
        side: 'bottom',
        title: 'There is no save button',
        says: 'It saves as you type, into a set of changes kept apart from the live site. Nothing here reaches readers by accident.',
      },
      {
        at: 'header.top .btn--primary, header.top .top__changes',
        side: 'bottom',
        title: 'Publishing is one step',
        says: 'It shows what will change, in the words a reader would use, and puts it in front of them. A publish can be undone from History.',
      },
    ],
  },
  publish: {
    label: 'The publish sheet',
    stops: [
      {
        at: '.publist',
        side: 'right',
        title: 'What will change',
        says: 'Every page you have touched, by its title. Press one to read the before and after, side by side.',
      },
      {
        at: '.sheet--publish .field',
        side: 'top',
        title: 'Why it changed',
        says: 'Kept with the publication, so History can tell you later what each one was for. It is optional and worth ten seconds.',
      },
      {
        at: '.publish__review',
        side: 'top',
        title: 'Or ask somebody first',
        says: 'Send it for review instead, and it waits for a verdict rather than reaching readers.',
      },
    ],
  },
  pulse: {
    label: 'Pulse',
    stops: [
      {
        at: '.screen__head, .pulse',
        side: 'bottom',
        title: 'Whether the documentation is still true',
        says: 'Pulse watches how old a page is, what changed around it, and what readers did next — then says which pages to look at.',
      },
      {
        at: '.pulse-row, .pulse__list',
        side: 'right',
        title: 'Fix it, or say it is still right',
        says: 'Marking a page reviewed is a promise about today. It resets the clock and changes nothing on the page.',
      },
    ],
  },
  inbox: {
    label: 'The inbox',
    stops: [
      {
        at: '.drawer__body, .inbox',
        side: 'left',
        title: 'What is waiting for you',
        says: 'Reviews asked of you, comments on your pages, and pages that have gone stale — the things with your name on them.',
      },
      {
        at: '.inbox__views, .drawer__head',
        side: 'bottom',
        title: 'Later is not lost',
        says: 'Anything you cannot do now can wait here without being forgotten, and the view you are in says which of those you are looking at.',
      },
    ],
  },
  review: {
    label: 'The changes drawer',
    stops: [
      {
        at: '.filelist, .drawer__block',
        side: 'left',
        title: 'Everything waiting to be published',
        says: 'One row per page you have touched, by its path. Press one to read the before and after; Discard puts a single page back the way readers have it.',
      },
      {
        at: '.drawer__draftpick, .drawer__head',
        side: 'bottom',
        title: 'Which set of changes you are in',
        says: 'Most of the time there is only one and it made itself. A team can keep several — one per piece of work — and switch between them here.',
      },
      {
        at: '.drawer__advanced',
        side: 'top',
        title: 'The git half, folded away',
        says: 'Commit, push and a pull request, for teams whose documentation lives in their own repository. Nobody else needs to open this.',
      },
    ],
  },
  settings: {
    label: 'Settings',
    stops: [
      {
        at: '.settings__scopes, .settings__nav',
        side: 'right',
        title: 'Three different subjects',
        says: 'This site, the organisation that owns it, and your own account. A workspace can hold several sites, so the card always says which one it is about.',
      },
      {
        at: '.settings__body, .settings__card',
        side: 'left',
        title: 'Each card saves itself',
        says: 'Changing one card never locks the rest of the screen, and each says its own result where you made the change.',
      },
    ],
  },
}
